import { useQuery } from '@tanstack/react-query';
import { Moment } from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'underscore';
import Api from '../../../../api/Api';
import { QUERY_KEYS } from '../../../../queries/queryKeys';
import useAuthStore from '../../../../store/authStore';
import useHomeStore from '../../../../store/homeStore';
import { useActiveMarker, useMarkerStore } from '../../../../store/markerStore';
import {
  DownloadInfoItem,
  PREPROCESSED_ANALYTICS,
} from '../../../../types/analytics';
import {
  DataAccess,
  SubscriptionType,
  WaterdataGroup,
} from '../../../../types/userGroupPermissions';
import { DownloadCheckboxKey } from '../../../../utils/common';
import { userGroupValidation } from '../../../../utils/userGroupValidation';

export const useDatasetMetadata = (
  namePanel: string,
  coords: number[],
  item: DownloadInfoItem,
  onDateChange: (dateRange: [Moment, Moment], defaultPanel: boolean) => void,
  handleChangePanelCategory: (key: string) => void
) => {
  const user = useAuthStore((state) => state.user);
  const setUserGroupPermissions = useAuthStore(
    (state) => state.setUserGroupPermissions
  );
  const overridePointData = useHomeStore((state) => state.overridePointData);

  const point = useActiveMarker();
  const setDatasetOffline = useMarkerStore((state) => state.setDatasetOffline);
  const setDatasetReport = useMarkerStore((state) => state.setDatasetReport);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [isTNSDatasetWarningVisible, setIsTNSDatasetWarningVisible] =
    useState(false);

  const metadataQuery = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, item?.datasetId],
    queryFn: async () => {
      if (!item?.datasetId) return null;
      return await Api.metadata.getMetadata(item.datasetId);
    },
    enabled: !!item?.datasetId,
  });

  const directDownloadQuery = useQuery({
    queryKey: [QUERY_KEYS.order.directDownload, item?.datasetId],
    queryFn: async () => {
      if (!item?.datasetId) return null;
      return await Api.moodV2Order.checkDirectDownload(item.datasetId);
    },
    enabled: !!item?.datasetId,
  });

  const isOfflineDataset = !!(
    metadataQuery.data && metadataQuery.data?.IsOffline
  );

  const hasDatasets = item.options.length > 0;

  useEffect(() => {
    if (!metadataQuery.data || !point || !item) return;

    const metaData = metadataQuery.data;

    const analyticDefs =
      metaData && metaData.AnalyticsDefinition
        ? metaData.AnalyticsDefinition.split('|')
        : [];

    const includesPreprocessed = analyticDefs.find(
      (analytic: any) =>
        analytic === PREPROCESSED_ANALYTICS.XLS || analytic === 'XLS'
    );

    setDatasetOffline(point.id, namePanel, isOfflineDataset);
    setDatasetReport(point.id, namePanel, includesPreprocessed !== undefined);

    if (metaData && metaData.TimeDefinition) {
      const permissions = userGroupValidation(
        user?.waterdataGroup
          ? WaterdataGroup[user.waterdataGroup as WaterdataGroup]
          : null,
        user?.subscriptionType
          ? SubscriptionType[user.subscriptionType as SubscriptionType]
          : null,
        metaData.TimeDefinition,
        user?.confidentialDatasetIds
      );

      setUserGroupPermissions((prevState) => {
        const currentValues = prevState[point.id] || {};
        return {
          ...prevState,
          [point.id]: {
            ...currentValues,
            [namePanel]: permissions,
          },
        };
      });
      if (permissions?.dataAccess === DataAccess.download) {
        setShowDownloadButton(permissions.dataAccess === DataAccess.download);
      }

      if (permissions?.dateRange) {
        onDateChange(permissions.dateRange, true);
      } else if (item?.dateRange) {
        onDateChange(item.dateRange, true);
      }
    }
  }, [
    metadataQuery.data,
    overridePointData,
    user,
    item.datasetId,
    isOfflineDataset,
  ]);

  const initializeActivePanel = useCallback(() => {
    if (!point || point.activeDatasetKey !== undefined) return;

    const initialPanel =
      point.activePanel && point.activePanel !== ''
        ? point.activePanel
        : DownloadCheckboxKey.SW;

    handleChangePanelCategory(initialPanel as string);
  }, [point, handleChangePanelCategory]);

  const showTNSWarning = useCallback((datasetId: string) => {
    if (!datasetId) return;

    const tns = [''];
    if (tns.includes(datasetId)) {
      let showWarning = true;
      const warningSeenDay = localStorage.getItem('tns');
      localStorage.setItem('tns', Date.now().toString());
      if (warningSeenDay) {
        const lastSeenDate = Number(warningSeenDay);
        if (!isNaN(lastSeenDate)) {
          const secondsPast = Date.now() - lastSeenDate / 1000;
          showWarning = false;
          if (secondsPast > 86400) {
            showWarning = false;
          }
        }
      }
      setIsTNSDatasetWarningVisible(showWarning);
    }
  }, []);

  const handleDownloadButtonVisibility = useCallback(
    (shouldDisplayButton: boolean) => {
      setShowDownloadButton(shouldDisplayButton);
    },
    []
  );

  useEffect(() => {
    if (directDownloadQuery.data) {
      handleDownloadButtonVisibility(!!directDownloadQuery.data?.Result);
    }
  }, [directDownloadQuery.data, handleDownloadButtonVisibility]);

  useEffect(() => {
    if (item.datasetId && item.datasetId && coords) {
      showTNSWarning(item.datasetId);

      initializeActivePanel();
    }
  }, [item.datasetId, coords, initializeActivePanel, showTNSWarning]);

  const onShowMetadata = useCallback(
    (datasetId: string, fn: (id: string) => void) => {
      if (isEmpty(datasetId)) {
        return;
      }
      fn(datasetId);
    },
    []
  );

  return {
    isTNSDatasetWarningVisible,
    setIsTNSDatasetWarningVisible,
    showDownloadButton,
    hasDatasets,
    datasetIsOffline: isOfflineDataset,
    onShowMetadata,
  };
};
